import {
  type Component,
  createComponent,
  createContext,
  createMemo,
  Index,
  type JSX,
  type ParentComponent,
  Show,
  useContext,
} from "solid-js";
import { unwrap } from "solid-js/store";
import { Dynamic } from "solid-js/web";
import {
  type ASTNode,
  astNodeText,
  type CodeBlockASTNode,
  type DocASTNode,
  type HeadingASTNode,
  type ItemASTNode,
  type LinkASTNode,
  type ListASTNode,
  type TableWrapperASTNode,
} from "../toastmark/ast";
import { CodeBlockWithCopy } from "./CodeBlockWithCopy";

export type MarkdownRendererComponents = {
  document: Component<{ node: DocASTNode }>;
  heading: Component<{ node: HeadingASTNode }>;
  paragraph: Component<{ node: ASTNode }>;
  text: Component<{ node: ASTNode }>;
  strong: Component<{ node: ASTNode }>;
  strike: Component<{ node: ASTNode }>;
  emph: Component<{ node: ASTNode }>;
  code: Component<{ node: ASTNode }>;
  link: Component<{ node: LinkASTNode }>;
  image: Component<{ node: LinkASTNode }>;
  list: Component<{ node: ListASTNode }>;
  item: Component<{ node: ItemASTNode }>;
  codeBlock: Component<{ node: CodeBlockASTNode }>;
  blockQuote: Component<{ node: ASTNode }>;
  thematicBreak: Component<{ node: ASTNode }>;
  htmlBlock: Component<{ node: ASTNode }>;
  customBlock: Component<{ node: ASTNode }>;
  table: Component<{ node: ASTNode }>;
  tableWrapper: Component<{ node: TableWrapperASTNode }>;
  tableHead: Component<{ node: ASTNode }>;
  tableBody: Component<{ node: ASTNode }>;
  tableRow: Component<{ node: ASTNode }>;
  tableCell: Component<{ node: ASTNode }>;
  customInline: Component<{ node: ASTNode }>;
  unknown: Component<{ node: ASTNode }>;
  softbreak: Component<{ node: ASTNode }>;
  linebreak: Component<{ node: ASTNode }>;
  tableDelimRow: Component<{ node: ASTNode }>;
  tableDelimCell: Component<{ node: ASTNode }>;
  htmlInline: Component<{ node: ASTNode }>;
};
export type MarkdownRendererClasses = {
  [K in keyof MarkdownRendererComponents]:
    | string
    | ((node: Parameters<MarkdownRendererComponents[K]>[0]["node"]) => string);
};

export type MarkdownRendererOptions = Partial<{
  /**
   * Custom components to use for rendering markdown nodes.
   *
   * @example
   * ```ts
   * components: {
   *   heading: (props: { node: HeadingASTNode }) => <h1>{props.node.literal}</h1>,
   * }
   * ```
   */
  components: Partial<MarkdownRendererComponents>;

  /**
   * Custom classes to use for styling markdown nodes.
   * Use them when overriding components is too much
   *
   * @example
   * ```ts
   * classes: {
   *   heading: "text-2xl font-bold",
   * }
   * ```
   */
  classes: Partial<MarkdownRendererClasses>;

  /**
   * Clipboard writer for the code block's copy button, resolving false when the
   * write failed. A host whose origin is not a secure context (chief's wails://
   * webview) has to supply one — navigator.clipboard rejects there. Absent →
   * the browser API.
   */
  copyText: (text: string) => Promise<boolean> | boolean;
}>;

type ClassResolver = (node: ASTNode) => string;

const createClassResolver = (options: MarkdownRendererOptions): ClassResolver => {
  const cache = new Map<string, string>();

  return (node: ASTNode) => {
    const nodeType = node.type;
    const classFn = options.classes?.[nodeType as keyof MarkdownRendererClasses];

    if (!classFn || typeof classFn === "string") {
      const cached = cache.get(nodeType);
      if (cached !== undefined) return cached;

      let result = `md-${nodeType}`;
      if (typeof classFn === "string") {
        result += ` ${classFn}`;
      }
      cache.set(nodeType, result);
      return result;
    }

    let result = `md-${nodeType}`;
    // @ts-expect-error - classFn is a function here
    result += ` ${classFn(node)}`;
    return result;
  };
};

const defaultClassResolver: ClassResolver = (node: ASTNode) => `md-${node.type}`;

type MarkdownRendererContextValue = {
  options: MarkdownRendererOptions & {
    components: MarkdownRendererComponents;
  };
  getNodeClass: ClassResolver;
  /**
   * Fixed for the lifetime of the provider: a document that is done changing renders once, so the
   * per-node memos and mapped signals the streaming path needs are skipped entirely.
   */
  isStatic: boolean;
};

const MarkdownRendererContext = createContext<MarkdownRendererContextValue | null>(null);

// The host's clipboard writer for every code block below it whose renderer
// options name none. Tool cards render markdown in a dozen places; threading the
// writer through each one's options would miss the next.
const MarkdownClipboardContext = createContext<MarkdownRendererOptions["copyText"]>();

export const MarkdownClipboardProvider: ParentComponent<{ copyText: MarkdownRendererOptions["copyText"] }> = (
  props
) => <MarkdownClipboardContext.Provider value={props.copyText}>{props.children}</MarkdownClipboardContext.Provider>;

const useNodeClass = () => {
  const ctx = useContext(MarkdownRendererContext);
  return ctx?.getNodeClass ?? defaultClassResolver;
};

const defaultComponents: MarkdownRendererComponents = {
  document: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <div class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </div>
    );
  },
  heading: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <Dynamic component={`h${props.node.level}`} class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </Dynamic>
    );
  },
  paragraph: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <p class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </p>
    );
  },
  text: (props) => {
    const getNodeClass = useNodeClass();
    return <span class={getNodeClass(props.node)}>{props.node.literal}</span>;
  },
  strong: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <strong class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </strong>
    );
  },
  strike: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <span class={getNodeClass(props.node)} style={{ "text-decoration": "line-through" }}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </span>
    );
  },
  emph: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <em class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </em>
    );
  },
  code: (props) => {
    const getNodeClass = useNodeClass();
    return <code class={getNodeClass(props.node)}>{props.node.literal}</code>;
  },
  link: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <a href={props.node.destination || "#"} class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </a>
    );
  },
  image: (props) => {
    const getNodeClass = useNodeClass();
    return <img src={props.node.destination || ""} alt={astNodeText(props.node)} class={getNodeClass(props.node)} />;
  },
  list: (props) => {
    const getNodeClass = useNodeClass();
    const ordered = () => props.node.listData?.type === "ordered";
    const start = () => {
      const first = props.node.listData?.start;
      return ordered() && first && first !== 1 ? first : undefined;
    };
    return (
      <Dynamic component={ordered() ? "ol" : "ul"} class={getNodeClass(props.node)} start={start()}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </Dynamic>
    );
  },
  item: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <li class={getNodeClass(props.node)}>
        {props.node.listData.task && (
          <input type="checkbox" checked={props.node.listData.checked} disabled class="markdown-task-checkbox" />
        )}
        <SolidStreamingMarkdownChildren node={props.node} />
      </li>
    );
  },
  codeBlock: (props) => {
    const getNodeClass = useNodeClass();
    // Read here and passed down rather than looked up inside CodeBlockWithCopy:
    // this module imports that component, so reaching back for the context from
    // it would close an import cycle.
    const ctx = useSolidStreamingMarkdownContext();
    const hostCopy = useContext(MarkdownClipboardContext);
    return (
      <CodeBlockWithCopy
        node={props.node}
        class={getNodeClass(props.node)}
        copyText={ctx.options.copyText ?? hostCopy}
      />
    );
  },
  blockQuote: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <blockquote class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </blockquote>
    );
  },
  thematicBreak: (props) => {
    const getNodeClass = useNodeClass();
    return <hr class={getNodeClass(props.node)} />;
  },
  htmlBlock: (props) => {
    const getNodeClass = useNodeClass();
    // Model-written markdown reaches every renderer, and a raw block injected as markup runs its
    // event handlers in the reader's session, so the block is shown as its source.
    const source = () => props.node.literal?.replace(/<!--[\s\S]*?(?:-->|$)/g, "") ?? "";
    return (
      <Show when={source().trim()}>
        <div class={getNodeClass(props.node)} style={{ "white-space": "pre-wrap" }}>
          {source()}
        </div>
      </Show>
    );
  },
  customBlock: (props) => {
    const getNodeClass = useNodeClass();
    return <div class={getNodeClass(props.node)}>{props.node.literal}</div>;
  },
  tableWrapper: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <div class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </div>
    );
  },
  table: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <table class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </table>
    );
  },
  tableHead: (props) => {
    const getNodeClass = useNodeClass();
    return props.node.children.length > 0 ? (
      <thead class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </thead>
    ) : null;
  },
  tableBody: (props) => {
    const getNodeClass = useNodeClass();
    return props.node.children.length > 0 ? (
      <tbody class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </tbody>
    ) : null;
  },
  tableRow: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <tr class={getNodeClass(props.node)}>
        <SolidStreamingMarkdownChildren node={props.node} />
      </tr>
    );
  },
  tableCell: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <Show when={props.node.children.length > 0 || props.node.literal}>
        <td class={getNodeClass(props.node)}>
          <SolidStreamingMarkdownChildren node={props.node} />
        </td>
      </Show>
    );
  },
  htmlInline: () => null,
  customInline: (props) => {
    const getNodeClass = useNodeClass();
    return <span class={getNodeClass(props.node)}>{props.node.literal}</span>;
  },
  unknown: (props) => {
    const getNodeClass = useNodeClass();
    return (
      <Show
        when={props.node.children.length > 0}
        fallback={<span class={getNodeClass(props.node)}>{props.node.literal}</span>}
      >
        <span class={getNodeClass(props.node)}>
          <SolidStreamingMarkdownChildren node={props.node} />
        </span>
      </Show>
    );
  },
  softbreak: () => <br />,
  linebreak: () => <br />,
  tableDelimRow: () => null,
  tableDelimCell: () => null,
};

const defaultContextValue: MarkdownRendererContextValue = {
  options: {
    components: {
      ...defaultComponents,
    },
  },
  getNodeClass: defaultClassResolver,
  isStatic: false,
};

const useSolidStreamingMarkdownContext = () => {
  const ctx = useContext(MarkdownRendererContext);
  return ctx ?? defaultContextValue;
};

export const SolidStreamingMarkdownContextProvider: ParentComponent<{
  options: MarkdownRendererOptions;
  static?: boolean;
}> = (props) => {
  const classResolver = createClassResolver(props.options);
  return (
    <MarkdownRendererContext.Provider
      value={{
        options: {
          ...props.options,
          components: {
            ...defaultComponents,
            ...(props.options?.components || {}),
          },
        },
        getNodeClass: classResolver,
        isStatic: !!props.static,
      }}
    >
      {props.children}
    </MarkdownRendererContext.Provider>
  );
};

const PARAGRAPH_CHUNK = 64;

// Index walks the whole list on every change, and through the store proxy every read is a trap, so it
// walks a raw copy instead, re-run only when the count changes; each slot reads its node through the
// proxy, so a replaced node still repaints. The memo keeps descendants from re-reading that path on
// every access.
const ChildRange = (props: { node: ASTNode; start: number; size: number }): JSX.Element => {
  const count = createMemo(() => Math.min(props.size, props.node.children.length - props.start));
  return (
    <Index each={unwrap(props.node.children).slice(props.start, props.start + count())}>
      {(_, i) => {
        const node = createMemo(() => {
          const child = props.node.children[props.start + i];
          if (!child) {
            throw new Error(`markdown: no child at index ${props.start + i} of a rendered range`);
          }
          return child;
        });
        return <ASTNodeRenderer node={node()} />;
      }}
    </Index>
  );
};

export const SolidStreamingMarkdownChildren = (props: { node: ASTNode }): JSX.Element => {
  if (useSolidStreamingMarkdownContext().isStatic) {
    return props.node.children.map((child) => <ASTNodeRenderer node={child} />);
  }
  if (props.node.type !== "paragraph") {
    return <ChildRange node={props.node} start={0} size={Infinity} />;
  }

  // Solid flattens every fragment under one element into a single child list and reconciles all of
  // it when any part changes, so a node added to a long paragraph cost as much as the paragraph. Past
  // the first chunk, each chunk gets an element of its own, and an append only touches the last one.
  // The spans carry no class or style, which leaves inline layout, selection and copied text as they
  // were; a paragraph short enough to fit one chunk renders without any.
  const extraChunks = createMemo(() => Math.max(0, Math.ceil(props.node.children.length / PARAGRAPH_CHUNK) - 1));
  return (
    <>
      <ChildRange node={props.node} start={0} size={PARAGRAPH_CHUNK} />
      <Index each={Array.from({ length: extraChunks() })}>
        {(_, c) => (
          <span>
            <ChildRange node={props.node} start={(c + 1) * PARAGRAPH_CHUNK} size={PARAGRAPH_CHUNK} />
          </span>
        )}
      </Index>
    </>
  );
};

export const ASTNodeRenderer = (props: { node: ASTNode }): JSX.Element => {
  const { options, isStatic } = useSolidStreamingMarkdownContext();
  const comp = options.components;

  if (isStatic) {
    // The node's type picks its component out of the map, a correlation the lookup type can't carry.
    const component = (comp[props.node.type as keyof MarkdownRendererComponents] || comp.unknown) as Component<{
      node: ASTNode;
    }>;
    return createComponent(component, { node: props.node });
  }

  return (
    <Dynamic component={comp[props.node.type as keyof MarkdownRendererComponents] || comp.unknown} node={props.node} />
  );
};
